import { ProviderConnectionType } from '../../../types/api/location.types';
import {
  BrandMenuProviderAssignmentDto,
  BrandMenuProviderResponse,
  BrandMenuResponse,
} from '../../../types/api/menu.types';
import { Provider } from '../../../types/shared.types';

export const MENU_NAME_MAX_LENGTH = 100;

export const defaultMenuOf = (menus: BrandMenuResponse[]) =>
  menus.find((m) => m.isDefault) ?? menus[0] ?? null;

/** Names are unique per brand, case-insensitively, after trimming. */
export const menuNameTaken = (
  menus: BrandMenuResponse[],
  name: string,
  exceptId?: number | null
) => {
  const wanted = name.trim().toLowerCase();
  return menus.some(
    (m) => m.id !== exceptId && m.name.trim().toLowerCase() === wanted
  );
};

/**
 * One row of the "menu per provider" list. The KitchenHub providers of a
 * brand always share one menu, so they collapse into a single row that holds
 * all of them; every other provider is a row of its own.
 */
export type MenuProviderChoice = {
  key: string;
  providers: Provider[];
  kitchenHubGroup: boolean;
  connectionType: ProviderConnectionType;
  menuId: number;
  /** False when the row follows the default menu instead of its own. */
  assigned: boolean;
};

export const KITCHEN_HUB_KEY = 'kitchenHub';

export const groupMenuProviders = (
  rows: BrandMenuProviderResponse[]
): MenuProviderChoice[] => {
  const group = rows.filter((r) => r.kitchenHubGroup);
  const singles: MenuProviderChoice[] = rows
    .filter((r) => !r.kitchenHubGroup)
    .map((r) => ({
      key: r.provider,
      providers: [r.provider],
      kitchenHubGroup: false,
      connectionType: r.connectionType,
      menuId: r.menuId,
      assigned: r.assigned,
    }));

  if (group.length === 0) return singles;

  const [first] = group;
  return [
    {
      key: KITCHEN_HUB_KEY,
      providers: group.map((r) => r.provider),
      kitchenHubGroup: true,
      connectionType: first.connectionType,
      menuId: first.menuId,
      assigned: group.some((r) => r.assigned),
    },
    ...singles,
  ];
};

/**
 * Only the rows whose menu changed go to the backend (the update is partial).
 * One provider stands for the whole KitchenHub group: the backend moves the
 * group when any member is sent, and sending them all with one menuId would
 * be the same thing said several times.
 */
export const menuAssignmentsFor = (
  choices: MenuProviderChoice[],
  picked: Record<string, number | undefined>
): BrandMenuProviderAssignmentDto[] =>
  choices
    .filter((c) => {
      const next = picked[c.key];
      return next != null && next !== c.menuId;
    })
    .map((c) => ({
      provider: c.providers[0],
      menuId: picked[c.key] as number,
    }));
